// public/js/navbar.js
// Premium Glassmorphism Navbar - 2025 Design
import { fetchJson, createIdempotencyKey, isGuestUser } from "./http/utils.js";
import { icon } from './shared/icons/render.js';
import { isBrowserOnline } from './utils/connectivity.js';

const __ = window.__ || (s => s);

// ── Theme Initialization ──────────────────────────────────
// Sync with app-shell.js: same localStorage key, same data-theme attribute.
// Default is 'dark' (platform design language).
(function initTheme() {
  const stored = (() => {
    try { return localStorage.getItem('cita-theme'); }
    catch { return null; }
  })();
  const VALID = ['light', 'dark', 'system'];
  const theme = (VALID.includes(stored) ? stored : null) || 'dark';
  let resolved = theme;
  if (theme === 'system') {
    resolved = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  document.documentElement.setAttribute('data-theme', resolved);

  // Re-resolve on OS change when theme is 'system'
  window.matchMedia?.('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      try {
        if (localStorage.getItem('cita-theme') === 'system') {
          const r = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
          document.documentElement.setAttribute('data-theme', r);
        }
      } catch { /* no-op */ }
    });

  // Cross-tab sync
  window.addEventListener('storage', (e) => {
    if (e.key !== 'cita-theme') return;
    const t = (['light', 'dark', 'system'].includes(e.newValue) ? e.newValue : null) || 'dark';
    const r = t === 'system'
      ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : t;
    document.documentElement.setAttribute('data-theme', r);
  });
})();

// ── Smooth page-enter after language switch reload ────────
if (sessionStorage.getItem('cita-lang-switch')) {
  sessionStorage.removeItem('cita-lang-switch');
  document.documentElement.classList.add('page-entering');
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => document.documentElement.classList.remove('page-entering'), 350);
  });
}

// ── Guest-Reset: Theme + Sprache auf Default zurücksetzen nach Session-Ablauf ──
// initTheme() oben läuft vor <body> und kann den Login-Status nicht prüfen.
// Hier auf DOMContentLoaded: wenn Gast → Preferences des vorherigen Users clearen.
document.addEventListener('DOMContentLoaded', () => {
  const loggedIn = document.body?.getAttribute('frappe-session-status') === 'logged-in'
    || (window.frappe?.boot?.user && window.frappe.boot.user !== 'Guest');
  if (loggedIn) return;
  try {
    if (localStorage.getItem('cita-theme')) {
      localStorage.removeItem('cita-theme');
      document.documentElement.setAttribute('data-theme', 'dark');
    }
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `preferred_language=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  } catch { /* no-op */ }
});

(() => {
  // Doppeltes Laden verhindern
  if (window.__CITA_NAVBAR_LOADED__) return;
  window.__CITA_NAVBAR_LOADED__ = true;

  /* ═══════════════════════════════════════════════════════════════════════════
     STATE & UTILITIES
  ═══════════════════════════════════════════════════════════════════════════ */
  const state = {
    profile: null,
    mediaMd: window.matchMedia('(min-width: 768px)'),
    lastActive: null,
    focusTrapRelease: null,
    scrolled: false,
    mobileMenuOpen: false,
    commandPalette: {
      open: false,
      items: [],
      filtered: [],
      query: '',
      highlight: 0,
      loading: false,
      loaded: false,
      request: null,
      cache: null,
      cacheTime: 0,
    },
    drawers: {
      notifications: { open: false, loaded: false, loading: false, controller: null, items: [], alerts: [], announcements: [], history: [], historyExpanded: false, unread_count: null },
    },
    _profileRequested: false,
  };

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const isMacLike = /Mac|iPod|iPhone|iPad/.test(navigator.platform || navigator.userAgentData?.platform || '');

  /** Debounce utility for input handlers */
  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     LOGOUT HANDLER
  ═══════════════════════════════════════════════════════════════════════════ */
  async function doLogout(redirectTo = '/login') {
    const token =
      (window.frappe && window.frappe.csrf_token) ||
      document.querySelector('meta[name="csrf_token"]')?.content ||
      '';
    const headers = token ? { 'X-Frappe-CSRF-Token': token } : {};
    try {
      await fetchJson('/api/method/logout', {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: {},
      });
    } catch (err) {
      console.warn('logout failed', err);
    } finally {
      try {
        window.localStorage?.removeItem('onboarding_debug');
        // Gast-Defaults wiederherstellen: Theme + Sprach-Cookie clearen
        window.localStorage?.removeItem('cita-theme');
        const secure = location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `preferred_language=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
      } catch (e) { /* ignore */ }
      window.location.href = sanitizeHref(redirectTo) || '/';
    }
  }

  // Delegate logout clicks
  document.addEventListener('click', (ev) => {
    const target = ev.target.closest('[data-cita-logout], a[href="/logout"], #logout-btn, #mobile-logout-btn');
    if (!target) return;
    ev.preventDefault();
    window.citaConfirm(__('Abmelden?'), {
      message: __('Möchtest du dich wirklich abmelden?'),
      icon: 'question',
      tone: 'primary',
      confirmLabel: __('Ja, abmelden'),
      cancelLabel: __('Abbrechen'),
    }).then((confirmed) => {
      if (confirmed) doLogout();
    });
  });

  /* ═══════════════════════════════════════════════════════════════════════════
     INITIALIZATION
  ═══════════════════════════════════════════════════════════════════════════ */
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  async function init() {
    // Check user login state
    const hintedUser = document.querySelector('meta[name="cita-user"]')?.content || 'Guest';
    if (hintedUser !== 'Guest' || document.body?.getAttribute('frappe-session-status') === 'logged-in') {
      await loadProfile();
    } else {
      state.profile = null;
      window.__citaProfile = null;
    }

    // Setup all components
    setupScrollBehavior();
    setupHeaderHeight();
    renderNavLinks();
    setupContextualBreadcrumb();
    renderMobileNav();
    setupMobileMenu();
    setupCommandPalette();
    setupNotifications();
    setupKeyboardShortcuts();
    // Hinweis: Profile wird von profile_widget.js gerendert

    // Update UI based on login state
    updateAuthUI();
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     SCROLL BEHAVIOR - Transparent → Glassmorphism
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupScrollBehavior() {
    const header = qs('#cita-header');
    if (!header) return;

    let ticking = false;
    const scrollThreshold = 50;

    function updateScroll() {
      const scrollY = window.scrollY;
      const shouldBeScrolled = scrollY > scrollThreshold;

      if (shouldBeScrolled !== state.scrolled) {
        state.scrolled = shouldBeScrolled;
        if (shouldBeScrolled) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
      ticking = false;
    }

    // Initial state
    updateScroll();

    // Scroll listener with requestAnimationFrame
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     HEADER HEIGHT CSS VARIABLE
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupHeaderHeight() {
    const header = qs('#cita-header');
    if (!header) return;

    function updateHeight() {
      document.documentElement.style.setProperty('--cita-header-h', `${header.offsetHeight || 64}px`);
    }

    updateHeight();

    if ('ResizeObserver' in window) {
      new ResizeObserver(updateHeight).observe(header);
    }
    window.addEventListener('resize', updateHeight, { passive: true });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     PROFILE LOADING
  ═══════════════════════════════════════════════════════════════════════════ */
  async function loadProfile() {
    const hinted = document.querySelector('meta[name="cita-user"]')?.content || 'Guest';
    const sessionHint = document.body?.getAttribute('frappe-session-status') === 'logged-in';
    if (hinted === 'Guest' && !sessionHint) {
      state.profile = null;
      window.__citaProfile = null;
      return;
    }

    if (state._profileRequested) return;
    state._profileRequested = true;

    // Welle W-ONLINE-1 Phase 0 (2026-05-16): isBrowserOnline()
    // honoriert window.__citaForceOnline + localStorage
    // "cita.force_online"="1" als Override-Kanaele.
    if (!isBrowserOnline()) {
      state.profile = null;
      window.__citaProfile = null;
      return;
    }

    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort('timeout'), 8000);

    try {
      const { payload } = await fetchJson(
        '/api/method/cita_events.api.core.cita_core_api.get_profile_widget_context',
        {
          method: 'GET',
          signal: ctl.signal,
          headers: { 'X-Idempotency-Key': createIdempotencyKey() },
        }
      );
      if (payload?.success !== true) {
        state.profile = null;
        window.__citaProfile = null;
        return;
      }
      state.profile = (payload?.data && typeof payload.data === 'object') ? payload.data : null;
      window.__citaProfile = state.profile;
      window.dispatchEvent(new CustomEvent('cita:profile', { detail: state.profile }));
    } catch {
      state.profile = null;
      window.__citaProfile = null;
    } finally {
      clearTimeout(t);
    }
  }

  function hasRole(role) {
    const roles = state.profile?.roles || [];
    return roles.includes(role);
  }

  function isOrganizer() {
    return !!state.profile?.is_organizer;
  }

  function isGuest() {
    const roles = state.profile?.roles || [];
    return roles.length === 0 || (roles.length === 1 && roles[0] === 'Guest');
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     AUTH UI UPDATE

     Hinweis: Das Profil-Widget wird von profile_widget.js gerendert.
     Die Sichtbarkeit von Login-Button und Profil wird über CSS gesteuert
     ([frappe-session-status] Attribut auf body).
  ═══════════════════════════════════════════════════════════════════════════ */
  function updateAuthUI() {
    // Die Sichtbarkeit wird komplett über CSS gesteuert basierend auf
    // [frappe-session-status="logged-in/logged-out"] auf dem body-Element.
    // profile_widget.js rendert das Profil-Widget in die Container.

    // Aktualisiere Mobile Menu Guest/Logged-in Actions
    const mobileGuestActions = qs('#mobile-guest-actions');
    const mobileLogoutBtn = qs('#mobile-logout-btn');

    if (isGuest()) {
      mobileGuestActions?.classList.remove('hidden');
      mobileLogoutBtn?.classList.add('hidden');
    } else {
      mobileGuestActions?.classList.add('hidden');
      mobileLogoutBtn?.classList.remove('hidden');
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     MENU MODEL

     New structure (2025):
     - Desktop logged-in: "Entdecken" dropdown + "Mein Bereich" dropdown
     - Desktop guest: "Entdecken" dropdown only
     - Organizer links under "Mein Bereich" for organizer members
  ═══════════════════════════════════════════════════════════════════════════ */
  /** Append active organizer slug to organizer paths as ?slug= query param. */
  function orgHref(path) {
    const slug = state.profile?.switcher?.active_slug;
    return slug ? `${path}?slug=${encodeURIComponent(slug)}` : path;
  }

  function menuModel() {
    const Extra = Array.isArray(window.__CITA_EXTRA_LINKS) ? window.__CITA_EXTRA_LINKS : [];

    // Dropdown "Entdecken" - visible for all (Mega-Menu)
    const entdeckenDropdown = {
      label: __('Entdecken'),
      href: '/event',
      visibleFor: 'all',
      mega: true,
      children: [
        { label: __('Events'), href: '/event', icon: 'calendar', description: __('Entdecke Events in deiner N\u00e4he') },
        { label: __('Preise'), href: '/pricing', icon: 'credit-card', description: __('Transparente Preise f\u00fcr Veranstalter') },
        { label: __('Hilfe & Support'), href: '/support', icon: 'help', description: __('FAQ, Anleitungen und Kontakt') },
      ]
    };

    // Dropdown "Mein Bereich" - only for logged-in users (Mega-Menu)
    const meinBereichChildren = [
      { label: __('Dashboard'), href: '/core/me', icon: 'home', description: __('Dein pers\u00f6nlicher \u00dcberblick') },
      { label: __('Meine Tickets'), href: '/my-events', icon: 'ticket', description: __('Gebuchte Events und Tickets') },
      { label: __('Profil bearbeiten'), href: '/core/account-verwalten', icon: 'user', description: __('Pers\u00f6nliche Daten und Einstellungen') },
    ];

    // Add Organizer section if user is member of an Event Organizer
    if (isOrganizer()) {
      meinBereichChildren.push(
        { divider: true, label: __('Organizer') },
        { label: __('Organizer Hub'), href: orgHref('/organizer'), icon: 'organizer', description: __('Dein Veranstalter-Dashboard') },
        { label: __('Events verwalten'), href: orgHref('/organizer/events'), icon: 'calendar', description: __('Erstellen, bearbeiten und verwalten') },
        { label: __('Team'), href: orgHref('/organizer/team'), icon: 'users', description: __('Teammitglieder und Rollen') },
        { label: __('Branding'), href: orgHref('/organizer/branding'), icon: 'palette', description: __('Logo, Farben und Design') },
        { divider: true, label: __('Finanzen') },
        { label: __('Abo & Plan'), href: orgHref('/organizer/subscription'), icon: 'credit-card', description: __('Dein aktueller Tarif') },
        { label: __('Zahlungen'), href: orgHref('/organizer/payment'), icon: 'wallet', description: __('Einnahmen und Auszahlungen') },
        { label: __('Rechnungen'), href: orgHref('/organizer/billing'), icon: 'receipt', description: __('Rechnungen und Belege') },
      );
    }

    const meinBereichDropdown = {
      label: __('Mein Bereich'),
      href: '/core/me',
      visibleFor: 'auth',
      mega: true,
      children: meinBereichChildren
    };

    // Guest: only Entdecken dropdown
    const guestLinks = [entdeckenDropdown];

    // Auth: Entdecken + Mein Bereich
    const authLinks = [entdeckenDropdown, meinBereichDropdown];

    return (isGuest() ? guestLinks : authLinks).concat(Extra);
  }

  function mobileMenuModel() {
    // Structure mirrors desktop: Entdecken + Mein Bereich (with Organizer section)
    const entdecken = {
      label: __('Entdecken'),
      icon: iconSvg('calendar'),
      visibleFor: 'all',
      links: [
        { label: __('Events'), href: '/event' },
        { label: __('Preise'), href: '/pricing' },
        { label: __('Support'), href: '/support' },
      ]
    };

    const meinBereich = {
      label: __('Mein Bereich'),
      icon: iconSvg('user'),
      visibleFor: 'auth',
      links: [
        { label: __('Dashboard'), href: '/core/me' },
        { label: __('Meine Tickets'), href: '/my-events' },
        { label: __('Profil bearbeiten'), href: '/core/account-verwalten' },
      ]
    };

    const organizer = {
      label: __('Organizer'),
      icon: iconSvg('organizer'),
      visibleFor: 'organizer',
      links: [
        { label: __('Organizer Hub'), href: orgHref('/organizer') },
        { label: __('Events verwalten'), href: orgHref('/organizer/events') },
        { label: __('Team'), href: orgHref('/organizer/team') },
        { label: __('Branding'), href: orgHref('/organizer/branding') },
        { label: __('Abo & Plan'), href: orgHref('/organizer/subscription') },
        { label: __('Zahlungen'), href: orgHref('/organizer/payment') },
        { label: __('Rechnungen'), href: orgHref('/organizer/billing') },
      ]
    };

    const guestExtras = {
      label: __('Service'),
      icon: iconSvg('ticket'),
      visibleFor: 'guest',
      links: [
        { label: __('Ticket anzeigen'), href: '/ticket/view' },
      ]
    };

    // Guest: Entdecken + Service
    const guestGroups = [entdecken, guestExtras];

    // Auth: Entdecken + Mein Bereich + Organizer (if role)
    const authGroups = [entdecken, meinBereich, organizer];

    return isGuest() ? guestGroups : authGroups;
  }

  function visibleFor(visibility) {
    if (!visibility || visibility === 'all') return true;
    if (visibility === 'guest') return isGuest();
    if (visibility === 'auth') return !isGuest();
    if (visibility === 'organizer') return isOrganizer();
    return hasRole(visibility);
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER DESKTOP NAV LINKS
  ═══════════════════════════════════════════════════════════════════════════ */
  function renderNavLinks() {
    const mount = qs('#nav-links');
    if (!mount) return;

    const links = menuModel().filter(l => visibleFor(l.visibleFor));

    mount.innerHTML = links.map((link, idx) => {
      const active = isActive(link.href);

      // Check if this link has children (dropdown)
      if (link.children && link.children.length > 0) {
        const dropdownId = `nav-dropdown-${idx}`;
        return `
          <div class="nav-dropdown-wrapper" data-dropdown-wrapper>
            <button type="button"
                    class="nav-link nav-link--dropdown${active ? ' active' : ''}"
                    aria-haspopup="true"
                    aria-expanded="false"
                    aria-controls="${dropdownId}"
                    data-dropdown-trigger>
              ${escapeHtml(link.label)}
              ${icon('chevronDown', { className: 'nav-dropdown-chevron' })}
            </button>
            <div id="${dropdownId}"
                 class="nav-dropdown"
                 role="menu"
                 aria-labelledby="${dropdownId}-trigger"
                 data-dropdown-menu>
              ${link.children.map(child => {
                // Handle dividers (section headers)
                if (child.divider) {
                  return `<div class="nav-dropdown-divider" role="separator">
                    <span class="nav-dropdown-divider-label">${escapeHtml(child.label)}</span>
                  </div>`;
                }
                // Mega-menu item with description
                const isMega = link.mega && child.description;
                return `<a href="${escapeAttr(child.href)}"
                   class="nav-dropdown-item${isActive(child.href) ? ' active' : ''}${isMega ? ' nav-dropdown-item--mega' : ''}"
                   role="menuitem">
                  ${child.icon ? `<span class="nav-dropdown-icon">${dropdownIcon(child.icon)}</span>` : ''}
                  ${isMega
                    ? `<div class="nav-dropdown-item-content">
                        <span class="nav-dropdown-label">${escapeHtml(child.label)}</span>
                        <span class="nav-dropdown-desc">${escapeHtml(child.description)}</span>
                      </div>`
                    : `<span class="nav-dropdown-label">${escapeHtml(child.label)}</span>`
                  }
                </a>`;
              }).join('')}
            </div>
          </div>
        `;
      }

      // Regular link without dropdown
      return `
        <a href="${escapeAttr(link.href)}"
           class="nav-link${active ? ' active' : ''}"
           role="menuitem">
          ${escapeHtml(link.label)}
        </a>
      `;
    }).join('');

    // Setup dropdown handlers after rendering
    setupNavDropdowns();
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     CONTEXTUAL BREADCRUMB (Organizer Event Pages)
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupContextualBreadcrumb() {
    const path = window.location.pathname;
    const mount = qs('#nav-links');
    if (!mount || !path.startsWith('/organizer/events/')) return;
    if (!isOrganizer()) return;

    // Extract event slug from path: /organizer/events/{slug}/...
    const parts = path.split('/');
    const eventSlug = parts[3];
    if (!eventSlug) return;

    // Find event title from profile context
    const activeEvents = state.profile?.active_events || [];
    const event = activeEvents.find(e => e.slug === eventSlug);
    const eventTitle = event?.title || eventSlug.replace(/-/g, ' ');

    const breadcrumb = document.createElement('div');
    breadcrumb.className = 'nav-context-breadcrumb';
    breadcrumb.innerHTML = `
      <span class="nav-context-separator" aria-hidden="true">/</span>
      <span class="nav-context-label" title="${escapeAttr(eventTitle)}">${escapeHtml(eventTitle)}</span>
    `;
    mount.after(breadcrumb);
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     DESKTOP DROPDOWN HANDLERS
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupNavDropdowns() {
    const wrappers = qsa('[data-dropdown-wrapper]');

    wrappers.forEach(wrapper => {
      const trigger = wrapper.querySelector('[data-dropdown-trigger]');
      const menu = wrapper.querySelector('[data-dropdown-menu]');
      if (!trigger || !menu) return;

      let closeTimeout = null;
      let isOpen = false;

      const open = () => {
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
        if (isOpen) return;
        isOpen = true;
        menu.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList.add('open');

        // Dropdown-Positioning: Viewport-Overflow verhindern
        requestAnimationFrame(() => {
          // Reset vorheriges Positioning
          menu.style.left = '';
          menu.style.right = '';
          menu.style.transform = '';

          const rect = menu.getBoundingClientRect();
          if (rect.right > window.innerWidth - 16) {
            menu.style.left = 'auto';
            menu.style.right = '0';
            menu.style.transform = 'translateY(0.5rem) scale(1)';
          } else if (rect.left < 16) {
            menu.style.left = '0';
            menu.style.transform = 'translateX(0) translateY(0.5rem) scale(1)';
          }
        });
      };

      const close = (immediate = false) => {
        if (!isOpen && !closeTimeout) return;
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
        if (immediate) {
          isOpen = false;
          menu.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.classList.remove('open');
        } else {
          closeTimeout = setTimeout(() => {
            isOpen = false;
            menu.classList.remove('open');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.classList.remove('open');
            closeTimeout = null;
          }, 150); // Small delay for smoother UX
        }
      };

      // Mouse events
      wrapper.addEventListener('mouseenter', open);
      wrapper.addEventListener('mouseleave', () => close());

      // Click for touch devices
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (isOpen) {
          close(true);
        } else {
          open();
        }
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
          const firstItem = menu.querySelector('.nav-dropdown-item');
          if (firstItem) firstItem.focus();
        } else if (e.key === 'Escape') {
          close(true);
          trigger.focus();
        }
      });

      menu.addEventListener('keydown', (e) => {
        const items = qsa('.nav-dropdown-item', menu);
        const currentIndex = items.indexOf(document.activeElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items[nextIndex]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items[prevIndex]?.focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close(true);
          trigger.focus();
        } else if (e.key === 'Tab') {
          close(true);
        }
      });

      // Close when clicking outside
      document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target) && isOpen) {
          close(true);
        }
      });
    });
  }

  function dropdownIcon(name) {
    const icons = {
      home: `${icon('home', { size: 16 })}`,
      calendar: `${icon('calendar', { size: 16 })}`,
      users: `${icon('users', { size: 16 })}`,
      user: `${icon('user', { size: 16 })}`,
      palette: `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="13.5" cy="6.5" r="0.5" fill="currentColor"/><circle cx="17.5" cy="10.5" r="0.5" fill="currentColor"/><circle cx="8.5" cy="7.5" r="0.5" fill="currentColor"/><circle cx="6.5" cy="12.5" r="0.5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"/></svg>`,
      'credit-card': `${icon('creditCard', { size: 16 })}`,
      trophy: `${icon('trophy', { size: 16 })}`,
      help: `${icon('helpCircle', { size: 16 })}`,
      ticket: `${icon('ticket', { size: 16 })}`,
      organizer: `${icon('grid3x3', { size: 16 })}`,
      globe: `${icon('globe', { size: 16 })}`,
      wallet: `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12V7H5a2 2 0 010-4h14v4"/><path d="M3 5v14a2 2 0 002 2h16v-5"/><path d="M18 12a2 2 0 100 4h4v-4h-4z"/></svg>`,
      receipt: `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z"/><path d="M16 8h-6a2 2 0 100 4h4a2 2 0 110 4H8"/><path d="M12 17V7"/></svg>`,
    };
    return icons[name] || '';
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER MOBILE NAV
  ═══════════════════════════════════════════════════════════════════════════ */
  function renderMobileNav() {
    const mount = qs('#mobile-nav-groups');
    if (!mount) return;

    const groups = mobileMenuModel()
      .filter(g => visibleFor(g.visibleFor))
      .map(g => ({
        ...g,
        links: g.links?.filter(l => visibleFor(l.visibleFor)) || []
      }))
      .filter(g => g.links.length > 0);

    mount.innerHTML = groups.map((g, idx) => `
      <details class="mobile-nav-group" ${idx === 0 ? 'open' : ''}>
        <summary class="mobile-nav-group-summary">
          <span class="mobile-nav-group-icon">${g.icon || ''}</span>
          <span class="mobile-nav-group-label">${escapeHtml(g.label)}</span>
          ${icon('chevronDown', { className: 'mobile-nav-group-chevron' })}
        </summary>
        <div class="mobile-nav-group-content">
          ${g.links.map(l => `
            <a href="${escapeAttr(l.href)}" class="mobile-nav-item${isActive(l.href) ? ' active' : ''}">
              ${escapeHtml(l.label)}
            </a>
          `).join('')}
        </div>
      </details>
    `).join('');
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     MOBILE MENU
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupMobileMenu() {
    const toggle = qs('#mobile-menu-toggle');
    const overlay = qs('#mobile-menu-overlay');
    const panel = qs('#mobile-menu-panel');
    const closeBtn = qs('#mobile-menu-close');

    if (!toggle || !overlay || !panel) return;

    function open() {
      state.lastActive = document.activeElement;
      state.mobileMenuOpen = true;
      // Close profile widget dropdown & notification drawer if open
      document.dispatchEvent(new CustomEvent('cita:close-profile-menus'));
      document.dispatchEvent(new CustomEvent('cita:close-notifications'));
      // Direct DOM fallback: force-close any floating profile dropdowns
      document.querySelectorAll('.nav-profile-dropdown.open').forEach(el => {
        el.classList.remove('open');
      });
      document.querySelectorAll('[data-cita-profile-toggle][aria-expanded="true"]').forEach(el => {
        el.setAttribute('aria-expanded', 'false');
      });
      overlay.classList.add('open');
      panel.classList.add('open');
      toggle.classList.add('open');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Menü schließen');
      document.body.classList.add('overflow-hidden');

      // Focus trap
      trapFocus(panel);

      // Focus first focusable
      requestAnimationFrame(() => {
        const firstFocusable = focusables(panel)[0];
        if (firstFocusable) firstFocusable.focus();
      });
    }

    function close() {
      state.mobileMenuOpen = false;
      overlay.classList.remove('open');
      panel.classList.remove('open');
      toggle.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Menü öffnen');
      document.body.classList.remove('overflow-hidden');
      releaseTrap();

      if (state.lastActive && typeof state.lastActive.focus === 'function') {
        state.lastActive.focus();
      }
    }

    toggle.addEventListener('click', () => {
      if (state.mobileMenuOpen) close();
      else open();
    });

    overlay.addEventListener('click', close);
    closeBtn?.addEventListener('click', close);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.mobileMenuOpen) {
        close();
      }
    });

    // Close on resize to desktop
    state.mediaMd.addEventListener('change', (ev) => {
      if (ev.matches && state.mobileMenuOpen) {
        close();
      }
    });

    // Close when clicking links inside menu
    panel.addEventListener('click', (e) => {
      if (e.target.closest('a[href]')) {
        close();
      }
    });

    // Close via event (e.g. when profile dropdown opens)
    document.addEventListener('cita:close-mobile-menu', () => {
      if (state.mobileMenuOpen) close();
    });

    // Mobile search: open command palette instead of form submit
    const mobileSearchInput = qs('#mobile-search-input');
    const mobileSearchForm = mobileSearchInput?.closest('form');
    if (mobileSearchInput && mobileSearchForm) {
      mobileSearchForm.addEventListener('submit', (e) => e.preventDefault());
      mobileSearchInput.addEventListener('focus', () => {
        close();
        requestAnimationFrame(() => {
          const cmdTrigger = qs('#cmd-trigger');
          cmdTrigger?.click();
        });
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     SEARCH
  ═══════════════════════════════════════════════════════════════════════════ */
  /* ═══════════════════════════════════════════════════════════════════════════
     COMMAND PALETTE
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupCommandPalette() {
    const root = qs('#cita-command-palette');
    const trigger = qs('#cmd-trigger');
    if (!root) return;

    renderCommandPaletteShell(root);

    const overlay = root.querySelector('[data-command-overlay]');
    const dialog = root.querySelector('[data-command-dialog]');
    const input = root.querySelector('#cita-command-input');
    const list = root.querySelector('[data-command-results]');
    const empty = root.querySelector('[data-command-empty]');

    const closePalette = () => {
      if (!state.commandPalette.open) return;
      root.classList.add('hidden');
      root.setAttribute('aria-hidden', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      state.commandPalette.open = false;
      document.body.classList.remove('overflow-hidden');
      state.commandPalette.query = '';
      if (input) input.value = '';
      releaseTrap();
      if (state.lastActive && typeof state.lastActive.focus === 'function') {
        state.lastActive.focus();
      }
    };

    const ensureHighlight = () => {
      if (!input || !list) return;
      const options = qsa('[data-command-idx]', list);
      if (!options.length) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      if (state.commandPalette.highlight >= options.length) {
        state.commandPalette.highlight = options.length - 1;
      }
      if (state.commandPalette.highlight < 0) state.commandPalette.highlight = options.length ? 0 : -1;
      options.forEach((option, idx) => {
        const active = idx === state.commandPalette.highlight;
        option.setAttribute('aria-selected', active ? 'true' : 'false');
        option.classList.toggle('highlighted', active);
        if (active) {
          option.scrollIntoView({ block: 'nearest' });
          input.setAttribute('aria-activedescendant', option.id);
        }
      });
    };

    const renderPalette = () => {
      if (!list || !empty) return;
      const items = state.commandPalette.filtered || [];
      if (!items.length) {
        list.innerHTML = '';
        empty.textContent = state.commandPalette.loaded
          ? (state.commandPalette.query ? __('Keine passenden Aktionen gefunden.') : __('Keine Einträge verfügbar.'))
          : __('Wird geladen …');
        empty.classList.remove('hidden');
        ensureHighlight();
        return;
      }

      let currentSection = '';
      const parts = [];
      items.forEach((item, idx) => {
        if (item.section !== currentSection) {
          currentSection = item.section;
          parts.push(`<div class="command-section-header">${escapeHtml(currentSection)}</div>`);
        }
        const active = idx === state.commandPalette.highlight;
        const optionId = `cita-command-option-${idx}`;
        parts.push(`
          <button type="button"
                  class="command-option${active ? ' highlighted' : ''}${item.accent ? ' accent' : ''}"
                  data-command-idx="${idx}"
                  data-href="${escapeAttr(item.href || '')}"
                  role="option"
                  id="${optionId}"
                  aria-selected="${active}"
                  tabindex="-1">
            <div class="command-option-content">
              <div class="command-option-label">${escapeHtml(item.label)}</div>
              ${item.description ? `<div class="command-option-description">${escapeHtml(item.description)}</div>` : ''}
            </div>
            ${item.badge ? `<span class="command-option-badge">${escapeHtml(item.badge)}</span>` : ''}
          </button>
        `);
      });

      list.innerHTML = parts.join('');
      empty.classList.add('hidden');
      ensureHighlight();
    };

    const filterPalette = (query) => {
      const q = (query || '').trim().toLowerCase();
      state.commandPalette.query = q;
      const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
      const items = state.commandPalette.items || [];
      const filtered = tokens.length
        ? items.filter(item => tokens.every(tok => item._haystack.includes(tok)))
        : [...items];
      state.commandPalette.filtered = filtered;
      state.commandPalette.highlight = filtered.length ? 0 : -1;
      renderPalette();
    };

    const activateIndex = (idx) => {
      const item = state.commandPalette.filtered?.[idx];
      if (!item) return;
      closePalette();
      const safeUrl = sanitizeHref(item.href);
      if (safeUrl) {
        window.location.assign(safeUrl);
      }
    };

    const PALETTE_CACHE_TTL = 30000; // 30 seconds cache

    const loadPalette = async () => {
      if (state.commandPalette.loading) {
        return;
      }

      // Check cache first
      const now = Date.now();
      if (state.commandPalette.cache && (now - state.commandPalette.cacheTime) < PALETTE_CACHE_TTL) {
        if (!state.commandPalette.loaded) {
          state.commandPalette.items = buildPaletteItems(state.commandPalette.cache);
          state.commandPalette.loaded = true;
          state.commandPalette.filtered = [...state.commandPalette.items];
          state.commandPalette.highlight = state.commandPalette.filtered.length ? 0 : -1;
          renderPalette();
        } else {
          filterPalette(state.commandPalette.query);
        }
        return;
      }

      if (state.commandPalette.loaded) {
        filterPalette(state.commandPalette.query);
        return;
      }

      state.commandPalette.loading = true;
      if (empty) {
        empty.setAttribute('role', 'status');
        empty.setAttribute('aria-live', 'polite');
        empty.innerHTML = `
          <div class="flex items-center gap-3 justify-center py-6 text-secondary">
            <svg class="animate-spin h-5 w-5 text-cyan-400" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"/>
            </svg>
            <span>Wird geladen…</span>
          </div>
        `;
      }
      const ctrl = new AbortController();
      state.commandPalette.request = ctrl;

      try {
        const { payload } = await fetchJson('/api/method/cita_events.api.core.navbar.get_command_palette_index', {
          method: 'GET',
          signal: ctrl.signal,
        });
        if (payload?.success !== true) {
          throw new Error(payload?.toast || __('Befehlspalette konnte nicht geladen werden.'));
        }
        const data = (payload.data && typeof payload.data === 'object') ? payload.data : {};
        // Update cache
        state.commandPalette.cache = data;
        state.commandPalette.cacheTime = Date.now();
        state.commandPalette.items = buildPaletteItems(data);
        state.commandPalette.loaded = true;
        state.commandPalette.filtered = [...state.commandPalette.items];
        state.commandPalette.highlight = state.commandPalette.filtered.length ? 0 : -1;
        renderPalette();
      } catch (error) {
        if (error?.name === 'AbortError') return;
        state.commandPalette.items = [];
        state.commandPalette.filtered = [];
        state.commandPalette.loaded = false;
        if (empty) {
          empty.innerHTML = `
            <div class="flex flex-col items-center gap-3 py-6 text-secondary">
              <span>Befehlspalette konnte nicht geladen werden.</span>
              <button type="button" class="text-cyan-400 hover:text-cyan-300 hover:underline focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-[var(--cita-bg-page)] rounded px-2 py-1" data-palette-retry>
                Erneut versuchen
              </button>
            </div>
          `;
          qs('[data-palette-retry]', empty)?.addEventListener('click', () => {
            state.commandPalette.loaded = false;
            loadPalette();
          });
        }
      } finally {
        if (state.commandPalette.request === ctrl) state.commandPalette.request = null;
        state.commandPalette.loading = false;
      }
    };

    const openPalette = async () => {
      state.lastActive = document.activeElement;
      root.classList.remove('hidden');
      root.setAttribute('aria-hidden', 'false');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      state.commandPalette.open = true;
      document.body.classList.add('overflow-hidden');
      trapFocus(dialog);
      filterPalette('');
      await loadPalette();
      requestAnimationFrame(() => {
        if (input) {
          input.value = '';
          input.focus({ preventScroll: true });
        }
      });
    };

    state.commandPalette.openPalette = openPalette;
    state.commandPalette.closePalette = closePalette;

    trigger?.addEventListener('click', (e) => {
      e.preventDefault();
      openPalette();
    });

    overlay?.addEventListener('click', closePalette);
    qsa('[data-command-close]', root).forEach(btn => btn.addEventListener('click', closePalette));

    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePalette();
      }
    });

    const debouncedFilter = debounce((q) => filterPalette(q), 200);
    input?.addEventListener('input', (e) => debouncedFilter(e.target.value || ''));
    input?.addEventListener('keydown', (e) => {
      const options = qsa('[data-command-idx]', list);
      if (e.key === 'ArrowDown') {
        if (!options.length) return;
        e.preventDefault();
        state.commandPalette.highlight = (state.commandPalette.highlight + 1) % options.length;
        ensureHighlight();
      } else if (e.key === 'ArrowUp') {
        if (!options.length) return;
        e.preventDefault();
        state.commandPalette.highlight = (state.commandPalette.highlight - 1 + options.length) % options.length;
        ensureHighlight();
      } else if (e.key === 'Enter') {
        if (state.commandPalette.highlight >= 0 && options[state.commandPalette.highlight]) {
          e.preventDefault();
          activateIndex(state.commandPalette.highlight);
        }
      }
    });

    list?.addEventListener('mousemove', (e) => {
      const option = e.target.closest('[data-command-idx]');
      if (!option) return;
      const idx = Number(option.getAttribute('data-command-idx'));
      if (!Number.isNaN(idx) && idx !== state.commandPalette.highlight) {
        state.commandPalette.highlight = idx;
        ensureHighlight();
      }
    });

    list?.addEventListener('click', (e) => {
      const option = e.target.closest('[data-command-idx]');
      if (!option) return;
      const idx = Number(option.getAttribute('data-command-idx'));
      if (Number.isNaN(idx)) return;
      e.preventDefault();
      activateIndex(idx);
    });
  }

  function renderCommandPaletteShell(root) {
    root.innerHTML = `
      <div class="command-palette-wrapper" data-command-wrapper>
        <div class="command-palette-overlay" data-command-overlay></div>
        <div class="command-palette-dialog" role="dialog" aria-modal="true" aria-labelledby="cita-command-title" data-command-dialog>
          <div class="command-palette-header">
            <h2 id="cita-command-title" class="command-palette-title">${__('Befehlspalette')}</h2>
            <div class="command-palette-shortcut" aria-hidden="true">
              <kbd>${shortcutLabel('K')}</kbd>
            </div>
            <button type="button" class="command-palette-close" data-command-close aria-label="Schließen">
              ${icon('xMark')}
            </button>
          </div>
          <div class="command-palette-search">
            <label for="cita-command-input" class="sr-only">${__('Suchen')}</label>
            ${icon('search', { className: 'command-palette-search-icon' })}
            <input id="cita-command-input" type="search" autocomplete="off" spellcheck="false" autocapitalize="none"
                   class="command-palette-input"
                   placeholder="${__('Suchen…')}">
          </div>
          <div class="command-palette-results" data-command-results role="listbox" aria-labelledby="cita-command-title"></div>
          <div class="command-palette-empty" data-command-empty>${__('Wird geladen …')}</div>
          <div class="command-palette-footer">
            ${__('Nutze')} <kbd>↑</kbd><kbd>↓</kbd> ${__('zum Navigieren,')} <kbd>Enter</kbd> ${__('zum Auswählen')}
          </div>
        </div>
      </div>
    `;
    root.classList.add('hidden');
    root.setAttribute('aria-hidden', 'true');
  }

  function buildPaletteItems(data) {
    const sections = [
      { key: 'actions', label: __('Aktionen') },
      { key: 'recent', label: __('Zuletzt geöffnet') },
      { key: 'entities', label: __('Objekte') },
      { key: 'ctas', label: __('Highlights') },
    ];
    const items = [];
    sections.forEach(({ key, label }) => {
      const rows = Array.isArray(data?.[key]) ? data[key] : [];
      rows.forEach((row) => {
        if (!row) return;
        const lbl = row.label || row.title || row.name || '';
        const description = row.description || row.subtitle || '';
        const keywords = Array.isArray(row.keywords) ? row.keywords : (Array.isArray(row.tags) ? row.tags : []);
        const sectionLabel = row.section || label;
        items.push({
          id: row.id || `${key}:${lbl}`.toLowerCase(),
          label: lbl,
          description,
          href: row.href || row.url || '',
          section: sectionLabel,
          accent: !!row.accent || key === 'ctas',
          badge: row.badge || row.status || '',
          keywords,
          _haystack: `${lbl} ${description} ${keywords.join(' ')} ${sectionLabel}`.toLowerCase(),
        });
      });
    });
    return items;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     NOTIFICATIONS
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupNotifications() {
    const root = qs('#cita-notification-drawer');
    if (!root) return;

    renderNotificationDrawer(root);

    const overlay = root.querySelector('[data-drawer-overlay]');
    const panel = root.querySelector('[data-drawer-panel]');
    const content = root.querySelector('[data-drawer-content]');
    const stateSlot = state.drawers.notifications;

    const closeDrawer = () => {
      if (!stateSlot.open) return;
      if (stateSlot.controller) {
        try { stateSlot.controller.abort(); } catch { /* noop */ }
      }
      root.classList.add('hidden');
      root.setAttribute('aria-hidden', 'true');
      stateSlot.open = false;
      document.body.classList.remove('overflow-hidden');
      releaseTrap();
      if (state.lastActive && typeof state.lastActive.focus === 'function') {
        state.lastActive.focus();
      }
    };

    const render = () => {
      if (!content) return;
      const items = stateSlot.items || [];
      const alerts = stateSlot.alerts || [];
      const announcements = stateSlot.announcements || [];

      // Check for dismissed announcements in localStorage
      const isDismissed = (code) => {
        try {
          return localStorage.getItem(`announcement_dismissed_${code}`) === 'true';
        } catch { return false; }
      };
      const visibleAnnouncements = announcements.filter(a => !a.is_dismissable || !isDismissed(a.code));

      // Render alerts first (System Alerts)
      let alertsHtml = '';
      if (alerts.length) {
        alertsHtml = `
          <div class="notification-alerts">
            ${alerts.map(alert => {
              const severityClass = alert.severity === 'Kritisch' ? 'critical' : (alert.severity === 'Warnung' ? 'warning' : 'info');
              const severityIcon = alert.severity === 'Kritisch'
                ? '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>'
                : alert.severity === 'Warnung'
                  ? '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>'
                  : '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>';
              return `
                <div class="notification-alert notification-alert--${severityClass}">
                  <div class="notification-alert-icon">
                    <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${severityIcon}</svg>
                  </div>
                  <div class="notification-alert-content">
                    <p class="notification-alert-title">${escapeHtml(alert.title)}</p>
                    ${alert.message ? `<p class="notification-alert-message">${escapeHtml(alert.message)}</p>` : ''}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        `;
      }

      // Render Platform Announcements
      let announcementsHtml = '';
      if (visibleAnnouncements.length) {
        const colorMap = {
          blue: 'notification-announcement--blue',
          green: 'notification-announcement--green',
          purple: 'notification-announcement--purple',
          amber: 'notification-announcement--amber',
          red: 'notification-announcement--red',
          transparent: 'notification-announcement--transparent',
        };
        // SVG Icons (konsistente Stroke-Icons, Lucide-Style)
        const iconSvgMap = {
          info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
          warn: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
          megaphone: '<path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 11-5.8-1.6"/>',
          sparkles: '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/><path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z"/>',
          wrench: '<path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>',
          gift: '<path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>',
          bell: '<path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>',
          star: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>',
          checkmark: '<circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>',
          calendar: '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
        };
        const defaultIconSvg = iconSvgMap.info;

        announcementsHtml = `
          <div class="notification-announcements">
            ${visibleAnnouncements.map(a => {
              const colorClass = colorMap[a.accent_color] || colorMap.blue;
              const iconPath = iconSvgMap[a.icon] || defaultIconSvg;
              return `
                <div class="notification-announcement ${colorClass}" data-announcement-code="${escapeAttr(a.code)}">
                  <div class="notification-announcement-icon">
                    <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${iconPath}</svg>
                  </div>
                  <div class="notification-announcement-content">
                    <div class="notification-announcement-header">
                      <p class="notification-announcement-title">${escapeHtml(a.title)}</p>
                      <span class="notification-announcement-type">${escapeHtml(a.announcement_type || '')}</span>
                    </div>
                    ${a.message ? `<p class="notification-announcement-message">${escapeHtml(a.message)}</p>` : ''}
                    ${sanitizeHref(a.action_url) ? `<a href="${escapeAttr(sanitizeHref(a.action_url))}" class="notification-announcement-action" data-drawer-link>${escapeHtml(a.action_label || __('Mehr erfahren'))} →</a>` : ''}
                  </div>
                  ${a.is_dismissable ? `
                    <button type="button" class="notification-announcement-dismiss" data-dismiss-announcement="${escapeAttr(a.code)}" aria-label="Ausblenden">
                      ${icon('xMark', { size: 16 })}
                    </button>
                  ` : ''}
                </div>
              `;
            }).join('')}
          </div>
        `;
      }

      const historyItems = stateSlot.history || [];

      // Only show empty message if NO content at all (including history)
      if (!items.length && !alerts.length && !visibleAnnouncements.length && !historyItems.length) {
        content.innerHTML = `<div class="notification-empty">
          ${icon('bell')}
          <p>${__('Keine neuen Benachrichtigungen')}</p>
          <p class="notification-empty-hint">${__('Du bist auf dem neuesten Stand')}</p>
        </div>`;
        return;
      }

      // Helper: Render a single notification item
      const renderItem = (item, idx) => {
        const title = escapeHtml(item?.title || item?.label || `${__('Benachrichtigung')} ${idx + 1}`);
        const description = item?.description || item?.body || '';
        const timestamp = item?.timestamp || item?.time_ago || '';
        const unread = item?.unread;
        const typeClass = item?.type ? `notification-item--${(item.type || '').toLowerCase().replace(/[^a-z0-9-]/g, '')}` : '';
        const notificationId = item?.id || '';
        const metaParts = [];
        if (description) metaParts.push(escapeHtml(description));
        if (timestamp) metaParts.push(escapeHtml(timestamp));
        const metaHtml = metaParts.length ? `<p class="notification-item-meta">${metaParts.join(' · ')}</p>` : '';
        const safeHref = sanitizeHref(item?.href);
        const wrapperTag = safeHref ? 'a' : 'div';
        const hrefAttr = safeHref ? ` href="${escapeAttr(safeHref)}" data-drawer-link` : '';
        return `
          <li class="notification-item${unread ? ' unread' : ''} ${typeClass}" data-notification-id="${escapeAttr(notificationId)}">
            <${wrapperTag} class="notification-item-wrapper"${hrefAttr}>
              <div class="notification-item-content">
                <p class="notification-item-title">${title}</p>
                ${metaHtml}
              </div>
              <div class="notification-item-actions">
                <button type="button"
                        class="notification-item-dismiss"
                        data-dismiss-notification="${escapeAttr(notificationId)}"
                        aria-label="Benachrichtigung ausblenden">
                  ${icon('xMark', { size: 16 })}
                </button>
              </div>
            </${wrapperTag}>
          </li>
        `;
      };

      // Group notifications by event_name (if available)
      let notificationsHtml = '';
      if (items.length) {
        const groups = new Map();
        const ungrouped = [];
        items.forEach((item, idx) => {
          const key = item?.event_name || item?.group || '';
          if (key) {
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push({ item, idx });
          } else {
            ungrouped.push({ item, idx });
          }
        });

        // Render: groups with >1 item get a collapsible header, rest renders flat
        const groupHtmlParts = [];
        for (const [groupName, groupItems] of groups) {
          const unreadCount = groupItems.filter(g => g.item?.unread).length;
          if (groupItems.length > 1) {
            const groupId = `notif-group-${escapeAttr(groupName.replace(/[^a-zA-Z0-9-]/g, ''))}`;
            groupHtmlParts.push(`
              <div class="notification-group">
                <button type="button" class="notification-group-header" data-toggle-notif-group="${groupId}" aria-expanded="true">
                  <span class="notification-group-name">${escapeHtml(groupName)}</span>
                  <span class="notification-group-count">${groupItems.length}${unreadCount > 0 ? ` · ${unreadCount} ${__('neu')}` : ''}</span>
                  ${icon('chevronDown', { size: 14, className: 'notification-group-chevron' })}
                </button>
                <ul class="notification-list notification-group-items" id="${groupId}">
                  ${groupItems.map(g => renderItem(g.item, g.idx)).join('')}
                </ul>
              </div>
            `);
          } else {
            // Single item in group — render flat (no header overhead)
            groupHtmlParts.push(renderItem(groupItems[0].item, groupItems[0].idx));
          }
        }
        // Ungrouped items
        const ungroupedHtml = ungrouped.map(g => renderItem(g.item, g.idx)).join('');

        const allItemsHtml = groupHtmlParts.join('') + ungroupedHtml;
        notificationsHtml = `<ul class="notification-list">${allItemsHtml}</ul>`;
      }

      // Render History Section (collapsed by default)
      let historyHtml = '';
      if (historyItems.length > 0) {
        const isExpanded = stateSlot.historyExpanded;
        historyHtml = `
          <div class="notification-history">
            <button type="button"
                    class="notification-history-toggle"
                    data-toggle-history
                    aria-expanded="${isExpanded}">
              <span class="notification-history-title">${__('Letzte Aktivitäten')}</span>
              <span class="notification-history-count">${historyItems.length}</span>
              ${icon('chevronDown', { size: 16, className: `notification-history-chevron ${isExpanded ? 'expanded' : ''}` })}
            </button>
            <div class="notification-history-content ${isExpanded ? '' : 'collapsed'}">
              <ul class="notification-history-list">
                ${historyItems.map(item => {
                  const statusIcon = item.status === 'Dismissed'
                    ? `<span class="history-status-icon dismissed" title="${__('Ausgeblendet')}">×</span>`
                    : `<span class="history-status-icon read" title="${__('Gelesen')}">✓</span>`;
                  return `
                    <li class="notification-history-item">
                      ${statusIcon}
                      <div class="notification-history-item-content">
                        <p class="notification-history-item-title">${escapeHtml(item.title || '')}</p>
                        <p class="notification-history-item-time">${escapeHtml(item.timestamp || '')}</p>
                      </div>
                      ${sanitizeHref(item.href) ? `<a href="${escapeAttr(sanitizeHref(item.href))}" class="notification-history-item-link" data-drawer-link>→</a>` : ''}
                    </li>
                  `;
                }).join('')}
              </ul>
            </div>
          </div>
        `;
      }

      content.innerHTML = alertsHtml + announcementsHtml + notificationsHtml + historyHtml;

      // Update badge on profile avatar(s)
      const unreadCount = stateSlot.unread_count ?? items.filter(i => i?.unread).length;
      updateBadge(unreadCount);
    };

    const load = async () => {
      if (stateSlot.loading) return;
      stateSlot.loading = true;
      if (content) content.innerHTML = `<p class="notification-loading">${__('Benachrichtigungen werden geladen …')}</p>`;

      if (stateSlot.controller) {
        try { stateSlot.controller.abort(); } catch { /* noop */ }
      }
      const ctrl = new AbortController();
      stateSlot.controller = ctrl;

      try {
        const { payload } = await fetchJson('/api/method/cita_events.api.core.navbar.get_nav_notifications', {
          method: 'GET',
          signal: ctrl.signal
        });
        if (payload?.success !== true) {
          throw new Error(payload?.toast || __('Benachrichtigungen konnten nicht geladen werden.'));
        }
        const data = (payload.data && typeof payload.data === 'object') ? payload.data : {};
        stateSlot.items = Array.isArray(data.items) ? data.items : [];
        stateSlot.alerts = Array.isArray(data.alerts) ? data.alerts : [];
        stateSlot.announcements = Array.isArray(data.announcements) ? data.announcements : [];
        stateSlot.history = Array.isArray(data.history) ? data.history : [];

        // Calculate actual unread count (subtract already-dismissed announcements)
        let serverCount = typeof data.unread_count === 'number' ? data.unread_count : 0;
        const dismissedCount = stateSlot.announcements.filter(a => {
          if (!a.is_dismissable) return false;
          try {
            return localStorage.getItem(`announcement_dismissed_${a.code}`) === 'true';
          } catch { return false; }
        }).length;
        stateSlot.unread_count = Math.max(0, serverCount - dismissedCount);

        // Update badge on profile avatar(s)
        updateBadge(stateSlot.unread_count);

        stateSlot.loaded = true;
        render();
      } catch (error) {
        if (error?.name === 'AbortError') return;
        stateSlot.items = [];
        stateSlot.alerts = [];
        stateSlot.announcements = [];
        stateSlot.history = [];
        stateSlot.unread_count = null;
        stateSlot.loaded = false;
        if (content) content.innerHTML = `<p class="notification-error">${__('Benachrichtigungen konnten nicht geladen werden.')}</p>`;
      } finally {
        if (stateSlot.controller === ctrl) stateSlot.controller = null;
        stateSlot.loading = false;
      }
    };

    const openDrawer = async () => {
      state.lastActive = document.activeElement;
      root.classList.remove('hidden');
      root.setAttribute('aria-hidden', 'false');
      stateSlot.open = true;
      document.body.classList.add('overflow-hidden');
      trapFocus(panel);
      const first = focusables(panel)[0];
      (first || panel).focus({ preventScroll: true });
      if (!stateSlot.loaded) await load();
      else render();

      // Setup swipe gestures for mobile after render
      setupSwipeToDissmiss();
    };

    // Swipe-to-dismiss setup for mobile devices
    const setupSwipeToDissmiss = () => {
      // Only on touch devices
      if (!('ontouchstart' in window)) return;

      const notificationItems = content.querySelectorAll('.notification-item');

      notificationItems.forEach(item => {
        if (item.dataset.swipeInitialized) return;
        item.dataset.swipeInitialized = 'true';

        let startX = 0;
        let currentX = 0;
        let isSwiping = false;
        let startY = 0;

        const SWIPE_THRESHOLD = 80;
        const SWIPE_MAX = 120;

        item.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          currentX = 0;
          isSwiping = false;
          item.style.transition = 'none';
        }, { passive: true });

        item.addEventListener('touchmove', (e) => {
          const deltaX = e.touches[0].clientX - startX;
          const deltaY = Math.abs(e.touches[0].clientY - startY);

          // Only horizontal swipes (left direction)
          if (!isSwiping && Math.abs(deltaX) > 10 && Math.abs(deltaX) > deltaY) {
            isSwiping = true;
          }

          if (isSwiping && deltaX < 0) { // Only swipe left
            e.preventDefault();
            currentX = Math.max(-SWIPE_MAX, deltaX);
            item.style.transform = `translateX(${currentX}px)`;
            item.style.opacity = String(1 - Math.abs(currentX) / SWIPE_MAX * 0.5);
          }
        }, { passive: false });

        item.addEventListener('touchend', async () => {
          item.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';

          if (Math.abs(currentX) > SWIPE_THRESHOLD) {
            // Trigger dismiss
            item.style.transform = 'translateX(-100%)';
            item.style.opacity = '0';

            const notificationId = item.dataset.notificationId;
            if (notificationId) {
              const result = await dismissNotificationApi(notificationId);
              if (result.success) {
                const dismissedItem = stateSlot.items.find(i => i.id === notificationId);
                setTimeout(() => {
                  item.remove();
                  stateSlot.items = stateSlot.items.filter(i => i.id !== notificationId);

                  if (dismissedItem) {
                    stateSlot.history.unshift({
                      ...dismissedItem,
                      status: 'Dismissed',
                      timestamp: __('Gerade eben')
                    });
                    if (stateSlot.history.length > 5) {
                      stateSlot.history.pop();
                    }
                  }

                  if (dismissedItem?.unread && stateSlot.unread_count > 0) {
                    stateSlot.unread_count -= 1;
                    updateBadge(stateSlot.unread_count);
                  }
                  render();
                }, 200);
              } else {
                // Reset on failure
                item.style.transform = 'translateX(0)';
                item.style.opacity = '1';
              }
            }
          } else {
            // Reset
            item.style.transform = 'translateX(0)';
            item.style.opacity = '1';
          }

          isSwiping = false;
          currentX = 0;
        }, { passive: true });
      });
    };

    // Event-based opening (triggered from profile widget)
    window.addEventListener('cita:open-notifications', () => openDrawer());
    // Event-based closing (triggered from mobile menu open)
    document.addEventListener('cita:close-notifications', () => closeDrawer());

    overlay?.addEventListener('click', closeDrawer);
    qsa('[data-drawer-close]', root).forEach(btn => btn.addEventListener('click', closeDrawer));

    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
      }
    });

    // Helper: Update badge count on profile avatar badges
    const updateBadge = (count) => {
      document.querySelectorAll('.profile-notification-badge').forEach(el => {
        if (count > 0) {
          el.textContent = count > 9 ? '9+' : String(count);
          el.style.display = '';
        } else {
          el.textContent = '';
          el.style.display = 'none';
        }
      });
      // Update drawer header badge
      document.querySelectorAll('[data-unread-badge]').forEach(el => {
        el.textContent = count > 0 ? (count > 9 ? '9+' : String(count)) : '';
      });
    };

    // Helper: Dismiss a notification via API
    const dismissNotificationApi = async (notificationId) => {
      try {
        const { payload } = await fetchJson('/api/method/cita_events.api.notifications.dismiss_notification', {
          method: 'POST',
          body: { notification_id: notificationId }
        });
        if (payload?.success !== true) {
          return {
            success: false,
            code: payload?.code || 'dismiss_failed',
            toast: payload?.toast || null,
          };
        }
        return payload;
      } catch (error) {
        console.error('Failed to dismiss notification:', error);
        return { success: false };
      }
    };

    content?.addEventListener('click', async (e) => {
      // Check dismiss buttons FIRST — prevent drawer-link from closing drawer on dismiss clicks
      const dismissAnnouncementBtn = e.target.closest('[data-dismiss-announcement]');
      const dismissNotificationBtn = e.target.closest('[data-dismiss-notification]');

      // Only close drawer on link click if NOT a dismiss action
      if (!dismissAnnouncementBtn && !dismissNotificationBtn && e.target.closest('[data-drawer-link]')) {
        closeDrawer();
      }

      // Handle announcement dismiss
      if (dismissAnnouncementBtn) {
        e.preventDefault();
        const code = dismissAnnouncementBtn.getAttribute('data-dismiss-announcement');
        // Security: Validate code format and length to prevent localStorage abuse
        if (code && code.length <= 100 && /^[a-zA-Z0-9_-]+$/.test(code)) {
          try {
            localStorage.setItem(`announcement_dismissed_${code}`, 'true');
          } catch { /* noop */ }
          const announcementEl = content.querySelector(`[data-announcement-code="${code}"]`);
          if (announcementEl) {
            announcementEl.remove();
          }
          // Update badge count after dismissal
          if (stateSlot.unread_count > 0) {
            stateSlot.unread_count -= 1;
            updateBadge(stateSlot.unread_count);
          }
        }
        return;
      }

      // Handle notification dismiss
      if (dismissNotificationBtn) {
        e.preventDefault();
        const notificationId = dismissNotificationBtn.getAttribute('data-dismiss-notification');
        if (!notificationId || notificationId.length > 200 || !/^[\w.:-]+$/.test(notificationId)) return;

        const itemEl = content.querySelector(`[data-notification-id="${notificationId}"]`);
        if (itemEl) {
          // Guard against double-click/double-tap
          if (itemEl.classList.contains('dismissing')) return;

          // Animate out
          itemEl.classList.add('dismissing');

          // API call
          const result = await dismissNotificationApi(notificationId);

          if (result.success) {
            // Find the dismissed item before removing from state
            const dismissedItem = stateSlot.items.find(i => i.id === notificationId);

            // Remove after animation (guard against drawer already closed)
            setTimeout(() => {
              if (!itemEl.parentElement) return;
              itemEl.remove();

              // Remove from items state
              stateSlot.items = stateSlot.items.filter(i => i.id !== notificationId);

              // Add to history at the beginning (if we have the item)
              if (dismissedItem) {
                stateSlot.history.unshift({
                  ...dismissedItem,
                  status: 'Dismissed',
                  timestamp: 'Gerade eben'
                });
                // Max 5 items in history
                if (stateSlot.history.length > 5) {
                  stateSlot.history.pop();
                }
              }

              // Update badge (only decrement if it was unread)
              if (dismissedItem?.unread && stateSlot.unread_count > 0) {
                stateSlot.unread_count -= 1;
                updateBadge(stateSlot.unread_count);
              }

              // Re-render for history update
              render();
            }, 300);
          } else {
            itemEl.classList.remove('dismissing');
          }
        }
        return;
      }

      // Handle notification group toggle (collapse/expand)
      const groupToggle = e.target.closest('[data-toggle-notif-group]');
      if (groupToggle) {
        e.preventDefault();
        const targetId = groupToggle.getAttribute('data-toggle-notif-group');
        if (!targetId || !/^[\w-]+$/.test(targetId)) return;
        const groupItems = content.querySelector(`#${targetId}`);
        const isExpanded = groupToggle.getAttribute('aria-expanded') === 'true';
        groupToggle.setAttribute('aria-expanded', String(!isExpanded));
        if (groupItems) groupItems.classList.toggle('collapsed', isExpanded);
        return;
      }

      // Handle history toggle
      const historyToggle = e.target.closest('[data-toggle-history]');
      if (historyToggle) {
        e.preventDefault();
        stateSlot.historyExpanded = !stateSlot.historyExpanded;

        const historyContent = content.querySelector('.notification-history-content');
        const chevron = historyToggle.querySelector('.notification-history-chevron');

        if (historyContent) {
          historyContent.classList.toggle('collapsed', !stateSlot.historyExpanded);
        }
        if (chevron) {
          chevron.classList.toggle('expanded', stateSlot.historyExpanded);
        }
        historyToggle.setAttribute('aria-expanded', String(stateSlot.historyExpanded));
        return;
      }
    });

    // Initial badge fetch on page load (lightweight, doesn't render drawer)
    // Skip for guests - they have no notifications
    if (isGuestUser()) {
      return;
    }

    const fetchBadgeCount = async () => {
      try {
        const { payload } = await fetchJson('/api/method/cita_events.api.core.navbar.get_nav_notifications', {
          method: 'GET'
        });
        if (payload?.success !== true) return;
        const data = (payload.data && typeof payload.data === 'object') ? payload.data : {};
        let serverCount = typeof data.unread_count === 'number' ? data.unread_count : 0;

        // Subtract already-dismissed announcements from count
        const announcements = Array.isArray(data.announcements) ? data.announcements : [];
        const dismissedCount = announcements.filter(a => {
          if (!a.is_dismissable) return false;
          try {
            return localStorage.getItem(`announcement_dismissed_${a.code}`) === 'true';
          } catch { return false; }
        }).length;
        const actualCount = Math.max(0, serverCount - dismissedCount);

        // Update profile avatar badges
        updateBadge(actualCount);

        // Store count for later use
        stateSlot.unread_count = actualCount;
      } catch {
        // Silently fail - badge just won't show
      }
    };

    // Fetch badge count after a short delay to not block page load
    setTimeout(fetchBadgeCount, 500);
  }

  function renderNotificationDrawer(root) {
    root.innerHTML = `
      <div class="notification-drawer-wrapper" data-drawer-wrapper>
        <div class="notification-drawer-overlay" data-drawer-overlay></div>
        <aside class="notification-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="notification-title" data-drawer-panel>
          <div class="notification-drawer-header">
            <h2 id="notification-title" class="notification-drawer-title">${__('Benachrichtigungen')} <span class="notification-drawer-badge" data-unread-badge></span></h2>
            <button type="button" class="notification-drawer-close" data-drawer-close aria-label="Schließen">
              ${icon('xMark')}
            </button>
          </div>
          <div class="notification-drawer-content" data-drawer-content></div>
        </aside>
      </div>
    `;
    root.classList.add('hidden');
    root.setAttribute('aria-hidden', 'true');
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     KEYBOARD SHORTCUTS
  ═══════════════════════════════════════════════════════════════════════════ */
  function setupKeyboardShortcuts() {
    window.addEventListener('keydown', (e) => {
      // Cmd/Ctrl + K → Command Palette
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (typeof state.commandPalette.openPalette === 'function') {
          state.commandPalette.openPalette();
        }
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     FOCUS TRAP
  ═══════════════════════════════════════════════════════════════════════════ */
  function focusables(root) {
    return qsa('a, button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])', root)
      .filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1 && (el.offsetParent !== null || el.getClientRects().length));
  }

  function trapFocus(root) {
    releaseTrap();
    if (!root) return;
    const fns = focusables(root);
    if (!fns.length) {
      state.focusTrapRelease = () => { state.focusTrapRelease = null; };
      return;
    }
    const first = fns[0], last = fns[fns.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (fns.length === 0) return e.preventDefault();
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    root.addEventListener('keydown', handler);
    state.focusTrapRelease = () => {
      root.removeEventListener('keydown', handler);
      state.focusTrapRelease = null;
    };
  }

  function releaseTrap() {
    if (typeof state.focusTrapRelease === 'function') {
      state.focusTrapRelease();
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     UTILITIES
  ═══════════════════════════════════════════════════════════════════════════ */
  function shortcutLabel(key = 'K') {
    const letter = String(key || '').toUpperCase();
    return isMacLike ? `⌘${letter}` : `Ctrl+${letter}`;
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value).replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#39;';
        default: return char;
      }
    });
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  /** Allow only same-origin absolute URLs and root-relative paths. */
  function sanitizeHref(url) {
    if (!url) return '';
    const trimmed = String(url).trim();
    // Root-relative paths (block protocol-relative //evil.com)
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return trimmed;
    }
    // Same-origin absolute URLs only
    if (/^https?:\/\//i.test(trimmed)) {
      try {
        const parsed = new URL(trimmed);
        if (parsed.origin === location.origin) return trimmed;
      } catch { /* invalid URL */ }
    }
    return '';
  }

  function isActive(href) {
    try {
      const u = new URL(href, location.origin);
      const current = location.pathname.replace(/\/+$/, '') || '/';
      const target = (u.pathname || '/').replace(/\/+$/, '') || '/';
      if (current === target) return true;
      return current.startsWith(`${target}/`);
    } catch { return false; }
  }

  function iconSvg(name) {
    const icons = {
      home: `${icon('home')}`,
      calendar: `${icon('calendar')}`,
      user: `${icon('user')}`,
      ticket: `${icon('ticket')}`,
      organizer: `${icon('grid3x3')}`,
      globe: `${icon('globe')}`,
      wallet: `<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 1 0 0 4h4v-4h-4z"/></svg>`,
      receipt: `<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17V7"/></svg>`,
      palette: `${icon('sparkles')}`,
      'credit-card': `${icon('creditCard')}`,
    };
    return icons[name] || '';
  }
})();
